import { useLazyQuery, gql } from '@apollo/client'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { Box, Fade } from '@mui/material'
import { FacebookAccount } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RouteChildrenProps } from 'react-router'
import { TransitionGroup } from 'react-transition-group'

import FilterContentLayoutWrapper from '@src/components/shared/FilterContentLayoutWrapper'
import { userSelector } from '@src/stores/app/selectors'
import EcoIcon from '@src/svgs/Eco'

import OrganicCampaignList from '../OrganicCampaignList'
import PremiumCampaignList from '../PremiumCampaignList'

export const ENABLE_INSIGHTS = gql`
  query EnableInsights($resourceType: String!, $resourceId: ID!) {
    getFacebookAccounts(
      resourceType: $resourceType
      resourceId: $resourceId
      params: { fields: ["token_is_valid", "manageable_pages"] }
    ) {
      id
      permissions {
        read_insights
      }
    }
  }
`

const Reporting: React.FC<React.PropsWithChildren<RouteChildrenProps>> = (
  props,
) => {
  const ref = useRef<HTMLDivElement>(null)
  const [currentRef, setCurrentRef] = useState<HTMLDivElement | null>(null)
  const [runQuery, { data, called }] = useLazyQuery(ENABLE_INSIGHTS)
  const user = useSelector(userSelector)
  const { campaignType, retailerAccountSlug } = props?.match?.params as {
    campaignType: string
    retailerAccountSlug: string
  }
  useEffect(() => {
    if (ref && ref.current) {
      setCurrentRef(ref.current)
    }
    return () => {
      setCurrentRef(null)
    }
  }, [ref])
  useEffect(() => {
    if (!user.id) {
      return
    }

    runQuery({
      variables: {
        resourceType: 'users',
        resourceId: user.id,
      },
    })
  }, [runQuery, user.id])
  useEffect(() => {
    if (!campaignType) {
      props?.history?.replace(
        `/retailer/reporting/${retailerAccountSlug}/all/organic`,
      )
    }
  }, [campaignType, props?.history, retailerAccountSlug])

  const userFacebookAccounts = (data || {}).getFacebookAccounts || []
  const hasInsightPermissions = userFacebookAccounts.some(
    (facebookAccount: FacebookAccount) => {
      return (
        facebookAccount.permissions &&
        facebookAccount.permissions['read_insights']
      )
    },
  )

  return (
    <FilterContentLayoutWrapper
      title={I18n.t('nav.reporting')}
      slot={{ filter: <div ref={ref} /> }}
      slotProps={{
        messageBar:
          called && !hasInsightPermissions && userFacebookAccounts.length
            ? {
                isDismissable: false,
                link: `/#/${retailerAccountSlug}/all/settings?tab=social_accounts&addNewFacebookPage=1`,
                linkText: I18n.t('reporting.enableInsightsCTA'),
                text: I18n.t('reporting.enableInsightsDescription'),
                type: 'info',
              }
            : undefined,

        tab: {
          selectedTab: campaignType,
          tabs: [
            {
              to: `/retailer/reporting/${retailerAccountSlug}/all/organic`,
              label: I18n.t('reporting.organicCampaigns'),
              value: 'organic',
            },
            {
              to: `/retailer/reporting/${retailerAccountSlug}/all/paid`,
              label: I18n.t('reporting.accelerateCampaigns'),
              value: 'paid',
            },
          ],
        },
        breadCrumb: {
          to: `/retailer/reporting/${retailerAccountSlug}/all/organic`,
        },
        breadCrumbMenu: {
          selected: campaignType,
          items: [
            {
              Icon: EcoIcon,
              label: I18n.t('shared.organic'),
              key: 'organic',
              to: `organic`,
            },
            {
              Icon: AttachMoneyIcon,
              label: I18n.t('shared.premium'),
              key: 'paid',
              to: `paid`,
            },
          ],
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <TransitionGroup>
          <Fade in={true} key={campaignType}>
            <Box
              bgcolor="background.paper"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                marginTop: '24px',
              }}
            >
              {currentRef && campaignType === 'paid' && (
                <PremiumCampaignList filterBarRef={ref.current} />
              )}
              {currentRef && campaignType !== 'paid' && (
                <OrganicCampaignList filterBarRef={ref.current} />
              )}
            </Box>
          </Fade>
        </TransitionGroup>
      </Box>
    </FilterContentLayoutWrapper>
  )
}

export default Reporting
